import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Message } from '../../../core/services/open-api.service';

// Define the Conversation interface here or in a separate models file
export interface Conversation {
  id: string;
  title: string;
  messages: Message[];
  chatThreadId?: string; // Added for chat id tracking
}

@Injectable({
  providedIn: 'root'
})
export class ChatService {
  // Use BehaviorSubject to hold and broadcast the list of conversations
  private readonly _conversations = new BehaviorSubject<Conversation[]>([]);
  readonly conversations$ = this._conversations.asObservable();

  // Use BehaviorSubject to hold and broadcast the currently selected conversation
  private readonly _currentConversation = new BehaviorSubject<Conversation | null>(null);
  readonly currentConversation$ = this._currentConversation.asObservable();

  constructor() {
    this.loadConversations();
  }

  // GETTERS to access the current value directly
  private get conversations(): Conversation[] {
    return this._conversations.getValue();
  }

  private get currentConversation(): Conversation | null {
    return this._currentConversation.getValue();
  }

  // --- All conversation management logic is now in this service ---

  loadConversations() {
    // Check if window is defined (for server-side rendering safety)
    if (typeof window !== 'undefined') {
      const savedConversations = localStorage.getItem('chat_conversations');
      let conversations: Conversation[] = [];
      if (savedConversations) {
        conversations = JSON.parse(savedConversations);
      }
      this._conversations.next(conversations); // Update stream

      // Set current conversation
      if (conversations.length > 0) {
        this._currentConversation.next(conversations[0]);
      } else {
        this.newChat(); // Create a new chat if none exist
      }
    } else {
      this.newChat();
    }
  }

  private saveConversations() {
    if (typeof window !== 'undefined') {
      // localStorage.setItem('chat_conversations', JSON.stringify(this.conversations));
    }
  }

  newChat() {
    const newConversation: Conversation = {
      id: Date.now().toString(),
      title: 'New Chat',
      messages: []
    };
    const updatedConversations = [newConversation, ...this.conversations];
    this._conversations.next(updatedConversations);
    this._currentConversation.next(newConversation);
    this.saveConversations();
  }

  selectConversation(conversation: Conversation) {
    this._currentConversation.next(conversation);
  }

  deleteConversation(conversationToDelete: Conversation) {
    const updatedConversations = this.conversations.filter(conv => conv.id !== conversationToDelete.id);
    this._conversations.next(updatedConversations);

    // If the deleted conversation was the active one, select a new one
    if (this.currentConversation?.id === conversationToDelete.id) {
      const newCurrent = updatedConversations.length > 0 ? updatedConversations[0] : null;
      this._currentConversation.next(newCurrent);
    }
    this.saveConversations();
    if (this.conversations.length === 0) {
      this.newChat(); // Create a new chat if none left
    }
  }

  // Method to add a message to the current conversation
  addMessage(message: Message) {
    const current = this.currentConversation;
    if (!current) return;

    current.messages.push(message);
    this._currentConversation.next(current); // Push the update
    this.saveConversations();
  }

  // Method to update the bot's streaming message
  updateBotMessage(messageIndex: number, newText: string) {
    const current = this.currentConversation;
    if (current && current.messages[messageIndex]) {
      current.messages[messageIndex].text = newText;
      this._currentConversation.next(current); // Push the update to stream
      this.saveConversations();
    }


  }



  // Method to finalize the conversation state after streaming
  finalizeConversation() {
    this.saveConversations();
  }

  // Method to update the conversation title
  updateConversationTitle(newTitle: string) {
    const current = this.currentConversation;
    if (current) {
      current.title = newTitle;
      this._currentConversation.next(current);
      this.saveConversations();
    }
  }



}
